import React, { useCallback } from 'react';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Spinner from '@atlaskit/spinner/spinner';
import Link from '@atlaskit/link/link';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { ERRORS, type ApplinksError } from './Errors.tsx';
import type { ApplicationLinkV3Status } from './CommonApplicationLinkCreationEntity.tsx';

const connectedStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.connected-status',
	defaultMessage: 'Connected',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link is successfully established and operating correctly.',
});

const notConnectedStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.not-connected-status',
	defaultMessage: 'Not Connected',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link exists but a connection to the remote application cannot be established.',
});

const accessDeniedStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.access-denied-status',
	defaultMessage: 'Access denied',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the current user does not have sufficient permissions to access the remote application.',
});

const authTokenRequiredMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-token-required-title',
	defaultMessage: 'Authenticate to see status',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the user must authenticate before the link status can be retrieved.',
});

const cloudConnectorAwaitingDcResponseStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.cloud-connector-awaiting-dc-response-status',
	defaultMessage: 'Awaiting DC response',
	description: 'Status lozenge for links with cloud connector awaiting DC response',
});

const incompatibleStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.incompatible-status',
	defaultMessage: 'Status unavailable',
	description: 'Status lozenge for incompatible application link',
});

const nonAtlassianStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.non-atlassian-status',
	defaultMessage: 'Non-Atlassian',
	description: 'Status lozenge for non-Atlassian application link',
});

const systemStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.system-status',
	defaultMessage: 'System',
	description: 'Status lozenge for system application link',
});

const networkErrorStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.network-error-status',
	defaultMessage: 'Network error',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link cannot be reached due to a network error.',
});

const creationFailedStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.creation-failed-status',
	defaultMessage: 'Creation failed',
	description: 'Status lozenge for links needing recreation',
});

const configurationIncompleteStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.configuration-incomplete-status',
	defaultMessage: 'Configuration incomplete',
	description: 'Status lozenge for links whose OAuth configuration is incomplete',
});

const inProgressStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.in-progress-status',
	defaultMessage: 'In progress',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link is currently being configured and is not yet fully set up.',
});

const configurationCompleteMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.configuration-complete-status',
	defaultMessage: 'Configuration complete',
	description: 'Status lozenge for configuration complete links',
});

const submissionFailedMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.submission-failed-status',
	defaultMessage: 'Submission failed',
	description: 'Status lozenge for links whose submission failed',
});

const deprecatedStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.deprecated-status',
	defaultMessage: 'Deprecated',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link uses a deprecated authentication type (e.g. Trusted Applications or Basic Access).',
});

const disabledStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.disabled-status',
	defaultMessage: 'Disabled',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link or its authentication is disabled.',
});

const configErrorStatusMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.config-error-status',
	defaultMessage: 'Config error',
	description:
		'The text is shown in a status lozenge badge on an application link row in the administration list when the link has an invalid or incorrect configuration.',
});

const messages = {
	connectedStatus: connectedStatusMessage,
	notConnectedStatus: notConnectedStatusMessage,
	accessDeniedStatus: accessDeniedStatusMessage,
	authTokenRequired: authTokenRequiredMessage,
	cloudConnectorAwaitingDcResponseStatus: cloudConnectorAwaitingDcResponseStatusMessage,
	incompatibleStatus: incompatibleStatusMessage,
	nonAtlassianStatus: nonAtlassianStatusMessage,
	systemStatus: systemStatusMessage,
	networkErrorStatus: networkErrorStatusMessage,
	creationFailedStatus: creationFailedStatusMessage,
	configurationIncompleteStatus: configurationIncompleteStatusMessage,
	inProgressStatus: inProgressStatusMessage,
	configurationComplete: configurationCompleteMessage,
	submissionFailed: submissionFailedMessage,
	deprecatedStatus: deprecatedStatusMessage,
	disabledStatus: disabledStatusMessage,
	configErrorStatus: configErrorStatusMessage,
};

export type ApplinkStatusCategory =
	| 'incompatible'
	| 'non-atlassian'
	| 'system'
	| 'network-error'
	| 'creation-failed'
	| 'configuration-incomplete'
	| 'in-progress'
	| 'configuration-complete'
	| 'submission-failed'
	| 'access-denied'
	| 'config-error'
	| 'deprecated-status'
	| 'disabled-status'
	| 'connected-status';

type StatusLozengeLoadingProps = {
	kind: 'loading';
};

type StatusLozengeErrorProps = {
	kind: 'error';
	err: ApplinksError;
	authorisationUri?: string;
};

type StatusLozengeWorkingProps = {
	kind: 'working';
};

type StatusLozengeNotConnectedProps = {
	kind: 'not-connected';
};

type StatusLozengeProps =
	| StatusLozengeLoadingProps
	| StatusLozengeErrorProps
	| StatusLozengeWorkingProps
	| StatusLozengeNotConnectedProps;

// Use StatusLozengeV3 if you have an ApplicationLinkV3Status, otherwise
// use this one to fake it (e.g. for the creation flow when we don't have
// an applink yet)
export const StatusLozenge = (props: StatusLozengeProps): React.JSX.Element | undefined => {
	const { formatMessage } = useIntl();

	if (props.kind === 'loading') {
		return <Spinner size="small" />;
	}

	if (props.kind === 'working') {
		return (
			<Lozenge appearance="success" isBold>
				{formatMessage(messages.connectedStatus)}
			</Lozenge>
		);
	}

	if (props.kind === 'not-connected') {
		return <Lozenge isBold>{formatMessage(messages.notConnectedStatus)}</Lozenge>;
	}

	if (props.kind === 'error') {
		const { err } = props;

		if (err.category === 'ACCESS_ERROR') {
			if (err.type === 'INSUFFICIENT_REMOTE_PERMISSION') {
				return (
					<Lozenge appearance="moved" isBold>
						{formatMessage(messages.accessDeniedStatus)}
					</Lozenge>
				);
			}
			// Authenticated status API -> prompt user to do OAuth dance
			if (props.authorisationUri) {
				return (
					<Link href={props.authorisationUri}>{formatMessage(messages.authTokenRequired)}</Link>
				);
			}
			// Fallback for ACCESS_ERROR cases that don't match above conditions
			return (
				<Lozenge appearance="removed" isBold>
					{formatMessage(messages.accessDeniedStatus)}
				</Lozenge>
			);
		}

		switch (err.type) {
			case 'CLOUD_CONNECTOR_AWAITING_DC_RESPONSE':
				return (
					<Lozenge appearance="removed" isBold>
						{formatMessage(messages.cloudConnectorAwaitingDcResponseStatus)}
					</Lozenge>
				);
			case 'INCOMPATIBLE':
				return (
					<Lozenge appearance="default" isBold>
						{formatMessage(messages.incompatibleStatus)}
					</Lozenge>
				);
			case 'NON_ATLASSIAN':
				return (
					<Lozenge appearance="default" isBold>
						{formatMessage(messages.nonAtlassianStatus)}
					</Lozenge>
				);
			case 'SYSTEM':
				return (
					<Lozenge appearance="default" isBold>
						{formatMessage(messages.systemStatus)}
					</Lozenge>
				);
			case 'NETWORK_ERROR':
			case 'UNKNOWN':
				return (
					<Lozenge appearance="removed" isBold>
						{formatMessage(messages.networkErrorStatus)}
					</Lozenge>
				);
			case 'CREATION_FAILED':
				return (
					<Lozenge appearance="removed" isBold>
						{formatMessage(messages.creationFailedStatus)}
					</Lozenge>
				);
			case 'CONFIGURATION_INCOMPLETE':
				return (
					<Lozenge appearance="removed" isBold>
						{formatMessage(messages.configurationIncompleteStatus)}
					</Lozenge>
				);
			case 'IN_PROGRESS':
				return (
					<Lozenge appearance="moved" isBold>
						{formatMessage(messages.inProgressStatus)}
					</Lozenge>
				);
			case 'CONFIGURATION_COMPLETE':
				return (
					<Lozenge appearance="inprogress" isBold>
						{formatMessage(messages.configurationComplete)}
					</Lozenge>
				);
			case 'SUBMISSION_FAILED':
				return (
					<Lozenge appearance="removed" isBold>
						{formatMessage(messages.submissionFailed)}
					</Lozenge>
				);
			case 'DEPRECATED':
				return (
					<Lozenge appearance="inprogress" isBold>
						{formatMessage(messages.deprecatedStatus)}
					</Lozenge>
				);
			case 'DISABLED':
				return (
					<Lozenge appearance="default" isBold>
						{formatMessage(messages.disabledStatus)}
					</Lozenge>
				);
			case 'CONFIG_ERROR':
			default:
				return (
					<Lozenge appearance="removed" isBold>
						{formatMessage(messages.configErrorStatus)}
					</Lozenge>
				);
		}
	}
};

type StatusLozengeV3Props = {
	status?: ApplicationLinkV3Status | null;
};

export const StatusLozengeV3 = (props: StatusLozengeV3Props): React.JSX.Element => {
	const wrapWithV3Status = useCallback(() => {
		if (!props.status) {
			return <StatusLozenge kind="loading" />;
		}

		if (props.status.working) {
			return <StatusLozenge kind="working" />;
		}

		const err = props.status.error ?? ERRORS.UNKNOWN;
		return <StatusLozenge kind="error" err={err} />;
	}, [props.status]);

	return wrapWithV3Status();
};
